/**
 * ChatMessage — Renders a single message bubble in the chat panel.
 *
 * User messages appear right-aligned (blue), assistant messages left-aligned
 * (gray). Assistant messages are rendered as markdown so bold, lists, and
 * other formatting display correctly. Tool call chips are shown below
 * assistant messages when the agent invoked tools during its response.
 */

import Markdown from 'react-markdown';

/**
 * Human-friendly tool name mapping for display.
 */
const TOOL_LABELS = {
  search_purchase_orders: 'Searched purchase orders',
  search_suppliers: 'Searched suppliers',
  compare_line_item_pricing: 'Compared pricing',
  extract_document: 'Extracted document'
};

/**
 * Format a timestamp as a relative time string (e.g., "2m ago", "1h ago").
 */
function formatRelativeTime(timestamp) {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  return then.toLocaleDateString();
}

/**
 * @param {{ message: { role: string, content: string, timestamp: string, toolCalls?: Array, isError?: boolean } }} props
 */
export default function ChatMessage({ message }) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[85%] ${isUser ? 'items-end' : 'items-start'}`}>
        {/* Message bubble */}
        <div
          className={`
            px-4 py-2.5 rounded-2xl text-sm leading-relaxed break-words
            ${isUser
              ? 'bg-blue-600 text-white rounded-br-md whitespace-pre-wrap'
              : message.isError
                ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-md whitespace-pre-wrap'
                : 'bg-gray-100 text-gray-900 rounded-bl-md chat-markdown'
            }
          `}
        >
          {/* Assistant messages render as markdown; user messages stay plain text */}
          {isUser || message.isError ? (
            message.content
          ) : (
            <Markdown>{message.content}</Markdown>
          )}
        </div>

        {/* Tool call chips — shown below assistant messages */}
        {!isUser && message.toolCalls?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5 ml-1">
            {message.toolCalls.map((tc, i) => (
              <span
                key={i}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-600 border border-blue-100"
              >
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {TOOL_LABELS[tc.name] || tc.name}
              </span>
            ))}
          </div>
        )}

        {/* Timestamp */}
        <p className={`text-xs text-gray-400 mt-1 ${isUser ? 'text-right mr-1' : 'ml-1'}`}>
          {formatRelativeTime(message.timestamp)}
        </p>
      </div>
    </div>
  );
}
